import { type ReactNode } from 'react';

import { cn } from '@/utils/cn';

export type IconWellTone =
  | 'neutral'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';
export type IconWellSize = 'sm' | 'md';

export interface IconWellProps {
  tone?: IconWellTone | undefined;
  size?: IconWellSize | undefined;
  children: ReactNode;
  className?: string | undefined;
}

const TONES: Record<IconWellTone, string> = {
  neutral: 'border-transparent bg-background text-text-muted',
  primary: 'border-transparent bg-background text-primary',
  secondary: 'border-transparent bg-background text-secondary-fg',
  success: 'border-transparent bg-background text-success',
  warning: 'border-transparent bg-background text-warning',
  danger: 'border-transparent bg-background text-danger',
  info: 'border-transparent bg-background text-info',
};

const SIZES: Record<IconWellSize, string> = {
  sm: 'size-10 rounded-md [&>svg]:size-5 [&>img]:size-5 [&>img]:object-contain',
  md: 'size-12 rounded-lg [&>svg]:size-6 [&>img]:size-6 [&>img]:object-contain',
};

/**
 * A token-coloured well for a decorative icon. Tone is never the only signal — the caller still
 * supplies a distinct glyph and adjacent text.
 */
export function IconWell({ tone = 'neutral', size = 'md', children, className }: IconWellProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center border',
        TONES[tone],
        SIZES[size],
        className,
      )}
    >
      {children}
    </span>
  );
}
